import { useApolloClient, useMutation } from '@apollo/client';
import { kebabCase } from 'lodash';
import { useCallback, useEffect, useRef, useState } from 'react';
import { FormProvider } from 'react-hook-form';
import { useIntl } from 'react-intl';

import { useAppContext } from '../../../../data/context/app-context';
import {
  AppFeature,
  GetHealthProviderFeaturesDocument,
  HealthProviderFeatureItemDataFragment,
  HealthProviderItemDataFragment,
  UpdateHealthProviderFeatureItemDocument,
  ValidateInput,
} from '../../../../data/types/api/graphql';
import { AlertMessageType } from '../../../../elements/alert-message/alert-message';
import {
  Button,
  ButtonIcon,
  ButtonType,
} from '../../../../elements/button/button';
import { InputSwitchController } from '../../../../elements/form/controllers/input-switch';
import { FormRow } from '../../../../elements/form/form';
import { CustomFormattedMessage } from '../../../../elements/intl/custom-formatted-message';
import { Modal, ModalWidth } from '../../../../elements/modal/modal';
import { ModalPrompt } from '../../../../elements/modal/modal-prompt';
import {
  buildHealthProviderFeatureUpdates,
  isDeleteChatEnabled,
} from '../../form/delete-chat-updates';
import { useHealthProviderFeatureForm } from '../../form/use-health-provider-feature-form';

type HealthProviderFeatureModal = {
  healthProvider: HealthProviderItemDataFragment;
  healthProviderFeatureItemData: HealthProviderFeatureItemDataFragment;
  /** All statuses of the practice; DELETE_CHAT is practice-wide. */
  healthProviderFeatureItems?: HealthProviderFeatureItemDataFragment[] | null;
  onSaved?: () => void;
  onCancel?: () => void;
  onClose?: () => void;
};

export function HealthProviderFeatureModal({
  healthProvider,
  healthProviderFeatureItemData,
  healthProviderFeatureItems,
  onSaved,
  onCancel,
  onClose,
}: HealthProviderFeatureModal) {
  const intl = useIntl();

  const { form, formIsValid, setErrors } = useHealthProviderFeatureForm();
  const { showAlertMessage } = useAppContext();

  // Starting a new chat requires replying to existing chats in the same status.
  const replyToExistingChats = form.watch('replyToExistingChats') === true;

  const [cancelPromptOpen, setCancelPromptOpen] = useState<boolean>(false);
  const onHandleCancel = useCallback(
    (confirmed: boolean = false) => {
      if (!confirmed && form.formState.isDirty) {
        setCancelPromptOpen(true);
      } else {
        onCancel?.();
        onClose?.();
      }
    },
    [form.formState.isDirty, onCancel, onClose]
  );

  // Reset once, when the modal opens. Saving refetches the features query, and a
  // reset on that refetch would throw away the user's edits and the errors on the form.
  const formInitialized = useRef(false);
  useEffect(() => {
    if (healthProviderFeatureItemData && !formInitialized.current) {
      formInitialized.current = true;
      form.reset({
        replyToExistingChats:
          healthProviderFeatureItemData.appFeatures.includes(
            AppFeature.ReplyToExistingChats
          ) ?? false,
        mind:
          healthProviderFeatureItemData.appFeatures.includes(AppFeature.Mind) ??
          false,
        startChat:
          healthProviderFeatureItemData.appFeatures.includes(
            AppFeature.StartChat
          ) &&
          healthProviderFeatureItemData.appFeatures.includes(
            AppFeature.ReplyToExistingChats
          ),
        startMedicationRequest:
          healthProviderFeatureItemData.appFeatures.includes(
            AppFeature.StartMedicationRequest
          ) ?? false,
        deleteChat: isDeleteChatEnabled(
          healthProviderFeatureItems ?? [healthProviderFeatureItemData]
        ),
      });
    }
  }, [healthProviderFeatureItemData, healthProviderFeatureItems, form]);

  const apolloClient = useApolloClient();
  const [updateHealthProviderFeatureItem] = useMutation(
    UpdateHealthProviderFeatureItemDocument
  );
  const [saving, setSaving] = useState<boolean>(false);

  const onHandleSubmit = useCallback(() => {
    form.handleSubmit(async () => {
      const values = form.getValues();
      const features = [];

      if (values.replyToExistingChats === true) {
        features.push(AppFeature.ReplyToExistingChats);
      }
      if (values.mind === true) {
        features.push(AppFeature.Mind);
      }
      if (
        values.startChat === true &&
        values.replyToExistingChats === true
      ) {
        features.push(AppFeature.StartChat);
      }
      if (values.startMedicationRequest === true) {
        features.push(AppFeature.StartMedicationRequest);
      }

      const updates = buildHealthProviderFeatureUpdates({
        items: healthProviderFeatureItems ?? [healthProviderFeatureItemData],
        availabilityStatus: healthProviderFeatureItemData.availabilityStatus,
        appFeatures: features,
        deleteChat: values.deleteChat === true,
      });

      setSaving(true);
      try {
        // Sequential, edited status first: when that one is refused, the
        // practice-wide DELETE_CHAT strip on the other statuses is never sent,
        // so the practice is not left half saved.
        type UpdateResult = Awaited<
          ReturnType<typeof updateHealthProviderFeatureItem>
        >;
        const failed = await updates.reduce<
          Promise<UpdateResult | undefined>
        >(
          async (previous, update) => {
            const previousFailure = await previous;
            if (previousFailure) {
              return previousFailure;
            }
            const result = await updateHealthProviderFeatureItem({
              variables: {
                features: update,
                healthProviderId: healthProvider.id ?? '',
              },
            });
            return result.data?.healthProviderFeaturesMutate?.boolean
              ? undefined
              : result;
          },
          Promise.resolve(undefined)
        );
        if (!failed) {
          showAlertMessage({
            type: AlertMessageType.SUCCESS,
            message: intl.formatMessage({
              id: 'health-provider-translation.health-provider-translation-update.success',
            }),
          });

          onSaved?.();

          onClose?.();
        } else {
          const errors = failed.data?.healthProviderFeaturesMutate?.errors;
          const missingRequiredFeature = errors?.some(
            error =>
              error.validation === ValidateInput.MissingRequiredFeature
          );

          if (missingRequiredFeature) {
            showAlertMessage({
              type: AlertMessageType.ERROR,
              message: intl.formatMessage({
                id: 'common.errors.missing-required-feature',
              }),
            });
            form.setError('startChat', {
              message: 'common.errors.missing-required-feature',
            });
          }

          setErrors(
            errors?.filter(
              error =>
                error.validation !== ValidateInput.MissingRequiredFeature
            )
          );
        }
      } catch (error) {
        showAlertMessage({
          type: AlertMessageType.ERROR,
          message: (error as Error).message,
        });
      } finally {
        setSaving(false);
        // One refetch after the whole save, also after a partial failure, so the
        // list (and a retry) works from what the server now holds.
        await apolloClient
          .refetchQueries({ include: [GetHealthProviderFeaturesDocument] })
          .catch(() => undefined);
      }
    })();
  }, [
    apolloClient,
    form,
    healthProvider.id,
    healthProviderFeatureItemData,
    healthProviderFeatureItems,
    intl,
    onClose,
    onSaved,
    setErrors,
    showAlertMessage,
    updateHealthProviderFeatureItem,
  ]);
  return (
    <>
      <Modal
        testId="health-provider-feature-modal"
        loading={false}
        title={healthProviderFeatureItemData.availabilityStatus ?? ''}
        width={ModalWidth.LARGE}
        onClose={onHandleCancel}
        buttons={
          <>
            <Button
              type={ButtonType.SECONDARY}
              inverted
              onClick={() => onHandleCancel(false)}
            >
              <CustomFormattedMessage id="common.cancel" />
            </Button>

            <Button
              icon={saving ? ButtonIcon.LOADING : ButtonIcon.NONE}
              disabled={!formIsValid || !form.formState.isDirty || saving}
              onClick={onHandleSubmit}
            >
              <CustomFormattedMessage id="common.change" />
            </Button>
          </>
        }
      >
        <FormProvider {...form}>
          <div className="description">
            <h4>
              <CustomFormattedMessage id="common.practice" />
            </h4>
            {healthProvider?.name ?? ''}
          </div>
          <FormRow>
            <FormRow>
              <InputSwitchController
                name="mind"
                label={
                  <CustomFormattedMessage
                    id={`common.feature.${kebabCase(AppFeature.Mind)}`}
                  />
                }
              />
            </FormRow>
            <InputSwitchController
              name="replyToExistingChats"
              onChange={value => {
                if (!value) {
                  form.setValue('startChat', false, { shouldDirty: true });
                }
              }}
              label={
                <CustomFormattedMessage
                  id={`common.feature.${kebabCase(
                    AppFeature.ReplyToExistingChats
                  )}`}
                />
              }
            />
          </FormRow>

          <FormRow>
            <InputSwitchController
              name="startChat"
              disabled={!replyToExistingChats}
              description={
                replyToExistingChats ? undefined : (
                  <CustomFormattedMessage id="health-provider-feature.start-chat.requires-reply" />
                )
              }
              label={
                <CustomFormattedMessage
                  id={`common.feature.${kebabCase(AppFeature.StartChat)}`}
                />
              }
            />
          </FormRow>

          <FormRow>
            <InputSwitchController
              name="startMedicationRequest"
              label={
                <CustomFormattedMessage
                  id={`common.feature.${kebabCase(
                    AppFeature.StartMedicationRequest
                  )}`}
                />
              }
            />
          </FormRow>

          <div className="description">
            <h4>
              <CustomFormattedMessage
                id="health-provider-feature.practice-wide"
              />
            </h4>
            <CustomFormattedMessage
              id="health-provider-feature.practice-wide.description"
            />
          </div>
          <FormRow>
            <InputSwitchController
              name="deleteChat"
              label={
                <CustomFormattedMessage
                  id={`common.feature.${kebabCase(AppFeature.DeleteChat)}`}
                />
              }
            />
          </FormRow>
        </FormProvider>
      </Modal>

      {cancelPromptOpen && (
        <ModalPrompt
          onConfirm={() => onHandleCancel(true)}
          onClose={() => setCancelPromptOpen(false)}
        >
          <p>
            <CustomFormattedMessage id="common.confirm-not-saved-changes" />
          </p>
        </ModalPrompt>
      )}
    </>
  );
}
